import { type CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';

import StaticPage from '~/components/StaticPage';
import {
  AdminBillingProvider,
  GroupSubscriptionsProvider,
  OrdersProvider,
  PodcastsProvider,
  useDevScenarioContext,
  usePreflightContext,
  useTabStateContext,
} from '~/context/SubscriptionManagement';
import { SUBSCRIBE_TABS, type TabKey } from '~/context/SubscriptionManagement/TabStateContext';
import type { Community, CurrentCommunityMember } from '~/interfaces/community';
import type { Publication } from '~/interfaces/publication';
import type { Site } from '~/interfaces/site';
import type { Subscription } from '~/interfaces/subscriptions';
import { getContrastingColor, hasMinimumContrast } from '~/utils/getContrastingColor';

import { AdminBillingSection } from './AdminBillingSection';
import { General } from './General/General';
import { GroupSubscriptionsSection } from './GroupSubscriptions/GroupSubscriptionsSection';
import { OrdersSection } from './OrdersSection';
import { PodcastsSection } from './PodcastsSection';

interface Props {
  site: Site;
  publication: Publication;
  subscription: Subscription;
  subscriberId: string;
  isLoggedIn: boolean;
  community?: Community | null;
  currentCommunityMember?: CurrentCommunityMember | null;
}

const PageInner = ({
  site,
  publication,
  subscription,
  subscriberId,
  isLoggedIn,
  community,
  currentCommunityMember,
}: Props) => {
  const { activeTab: activeTabParam, setActiveTab } = useTabStateContext();
  const { hasOrders, hasPodcasts, hasGroups, hasAdminBilling } = usePreflightContext();

  const { t } = useTranslation();

  // Visible-tab list derived from the BE-resolved preflight flags (`general` always shows).
  const filteredTabs = useMemo<TabKey[]>(
    () =>
      SUBSCRIBE_TABS.filter((tab) => {
        if (tab === 'orders') return hasOrders;
        if (tab === 'podcasts') return hasPodcasts;
        if (tab === 'groups') return hasGroups;
        if (tab === 'billing') return hasAdminBilling;
        return true;
      }),
    [hasOrders, hasPodcasts, hasGroups, hasAdminBilling],
  );

  const showTabs = isLoggedIn && (hasOrders || hasPodcasts || hasGroups || hasAdminBilling);

  // `TabStateProvider` resolves `activeTabParam` from `?tab=` without checking visibility, so fall
  // back to `general` when the requested tab isn't available (e.g. a shared `?tab=groups` URL).
  const activeTab = useMemo<TabKey>(
    () => (showTabs && filteredTabs.includes(activeTabParam) ? activeTabParam : 'general'),
    [showTabs, filteredTabs, activeTabParam],
  );

  // BE-resolved Flipper flag (BEE-18968). Default `false` so a missing field
  // (older Swarm response) keeps the tab off rather than leaking it to every
  // publication. Used here to gate the admin-billing fan-out; the group data
  // itself lives in GroupSubscriptionsProvider.
  const groupSubscriptionsEnabled = publication.group_subscriptions_enabled ?? false;

  // DEV-only General-tab scenario override (BEE-18984). The scenario-applied subscription feeds
  // the General tab; the scenario picker itself now lives in `General`.
  const { effectiveSubscription: generalSubscription } = useDevScenarioContext();

  const primaryThemeBackgroundColor = site.primary_theme.primary_color; // labeled as accent in theme settings
  const primaryThemeTextOnBackgroundColor = hasMinimumContrast(
    site.primary_theme.background_color,
    site.primary_theme.primary_color,
  )
    ? site.primary_theme.background_color
    : getContrastingColor(site.primary_theme.primary_color); // fallback to black/white when theme colors lack contrast

  return (
    <div
      className="bg-gray-100"
      style={
        {
          '--pt-background-color': primaryThemeBackgroundColor,
          '--pt-text-on-background-color': primaryThemeTextOnBackgroundColor,
        } as CSSProperties
      }
    >
      <StaticPage site={site} forceShowNavbar forceShowFooter>
        <div className="flex flex-col gap-6 w-full px-6 py-4 mx-auto min-h-dvh h-auto sm:py-8 md:px-0 md:w-2/3">
          {/*
            Dev-only mock controls now live in the floating 🐝 bee panel
            mounted from root.tsx (see app/components/DevToolsPanel/). After a
            toggle, Remix's revalidator re-runs the active page's loaders, so
            this page automatically picks up new mock state without an explicit
            refetch hook.
          */}

          {showTabs && (
            <div role="tablist" className="flex w-fit flex-wrap gap-1 rounded-lg bg-gray-100 p-1">
              {filteredTabs.map((tab) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab}
                  key={tab}
                  className={classNames(
                    'text-sm font-medium whitespace-nowrap rounded-lg px-4 py-2 transition-colors',
                    {
                      'bg-white text-gray-950 shadow-sm': activeTab === tab,
                      'text-gray-600 hover:bg-gray-50 hover:text-gray-900': activeTab !== tab,
                    },
                  )}
                  onClick={() => setActiveTab(tab)}
                >
                  {t(`manage.tabs.${tab}`)}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'general' && (
            <General
              site={site}
              publication={publication}
              subscription={generalSubscription}
              subscriberId={subscriberId}
              isLoggedIn={isLoggedIn}
              community={community}
              currentCommunityMember={currentCommunityMember}
            />
          )}

          {activeTab === 'orders' && (
            <OrdersProvider
              publication={publication}
              subscription={subscription}
              subscriberId={subscriberId}
              isLoggedIn={isLoggedIn}
            >
              <OrdersSection />
            </OrdersProvider>
          )}

          {activeTab === 'groups' && (
            <GroupSubscriptionsProvider publication={publication} subscriberId={subscriberId} isLoggedIn={isLoggedIn}>
              <GroupSubscriptionsSection />
            </GroupSubscriptionsProvider>
          )}

          {activeTab === 'billing' && hasAdminBilling && (
            <AdminBillingProvider
              isLoggedIn={isLoggedIn}
              groupSubscriptionsEnabled={groupSubscriptionsEnabled}
              subscriberId={subscriberId}
            >
              <AdminBillingSection />
            </AdminBillingProvider>
          )}

          {activeTab === 'podcasts' && (
            <PodcastsProvider subscription={subscription} subscriberId={subscriberId}>
              <PodcastsSection />
            </PodcastsProvider>
          )}
        </div>
      </StaticPage>
    </div>
  );
};

export default PageInner;
